import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClients, createClient, generateAIProposal, createProposal, getTemplates } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/ui/page-header';
import { SectionRule } from '@/components/ui/section-rule';
import { Field } from '@/components/ui/field';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ArrowLeft, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function NewProposal() {
  const [step, setStep] = useState(1);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [meetingNotes, setMeetingNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [content, setContent] = useState(null);
  const [title, setTitle] = useState('');
  const [totalAmount, setTotalAmount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showAddClient, setShowAddClient] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', email: '', company: '' });
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    getClients().then(r => setClients(r.data)).catch(() => {});
    getTemplates().then(r => setTemplates(r.data)).catch(() => {});
  }, []);

  const handleAddClient = async () => {
    if (!newClient.name.trim()) return;
    try {
      const res = await createClient(newClient);
      setClients(prev => [res.data, ...prev]);
      setSelectedClient(res.data.id);
      setShowAddClient(false);
      setNewClient({ name: '', email: '', company: '' });
      toast.success('Client added');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to add client';
      toast.error(msg);
    }
  };

  const handleGenerate = async () => {
    if (!selectedClient) { toast.error('Select a client first'); return; }
    if (!meetingNotes.trim()) { toast.error('Enter meeting notes'); return; }
    setGenerating(true);
    try {
      const payload = {
        client_id: selectedClient,
        meeting_notes: meetingNotes,
        business_name: user?.business_name || user?.name,
      };
      if (selectedTemplate && selectedTemplate !== 'none') {
        const tpl = templates.find(t => t.id === selectedTemplate);
        if (tpl?.template_content) payload.template_content = tpl.template_content;
      }
      const res = await generateAIProposal(payload);
      setContent(res.data.content);
      setTitle(res.data.suggested_title || '');
      setTotalAmount(res.data.total_amount || 0);
      setStep(3);
      toast.success('Proposal generated!');
    } catch (err) {
      toast.error('Failed to generate proposal. Please try again.');
    }
    setGenerating(false);
  };

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Enter a proposal title'); return; }
    setSaving(true);
    try {
      const res = await createProposal({
        client_id: selectedClient,
        title,
        content: {
          ...content,
          pricing: (content.pricing || []).map(item => ({ ...item, amount: parseFloat(item.amount) || 0 })),
        },
        total_amount: totalAmount,
        currency: 'USD'
      });
      toast.success('Proposal saved as draft');
      navigate(`/proposals/${res.data.id}/edit`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to save proposal';
      toast.error(msg);
    }
    setSaving(false);
  };

  return (
    <div className="mx-auto max-w-[760px]" data-testid="new-proposal-page">
      <button
        onClick={() => navigate('/proposals')}
        className="mb-4 inline-flex items-center gap-1.5 text-small text-ink-muted transition-colors hover:text-forest"
        data-testid="back-to-proposals"
      >
        <ArrowLeft strokeWidth={1.5} className="h-3.5 w-3.5" /> Proposals
      </button>

      <PageHeader title="New proposal" description={`Step ${step} of 3`} />

      <div className="flex gap-2" aria-hidden="true">
        {[1, 2, 3].map(s => (
          <div key={s} className={`h-0.5 flex-1 transition-colors ${step >= s ? 'bg-forest' : 'bg-line'}`} />
        ))}
      </div>

      {/* Step 1: Select Client */}
      {step === 1 && (
        <section className="mt-8" data-testid="step-1">
          <SectionRule label="Client" />
          <Field label="Client" htmlFor="proposal-client" hint="Required">
            <Select value={selectedClient} onValueChange={setSelectedClient}>
              <SelectTrigger id="proposal-client" data-testid="select-client">
                <SelectValue placeholder="Choose a client" />
              </SelectTrigger>
              <SelectContent>
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}{c.company ? ` (${c.company})` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Button variant="ghost" size="sm" onClick={() => setShowAddClient(true)} className="mt-3" data-testid="add-new-client-btn">
            Add new client
          </Button>

          <div className="sticky bottom-0 z-10 mt-10 flex justify-end gap-3 border-t border-line bg-paper/95 py-4">
            <Button variant="ghost" onClick={() => navigate('/proposals')}>Cancel</Button>
            <Button
              onClick={() => { if (!selectedClient) { toast.error('Select a client'); return; } setStep(2); }}
              data-testid="step-1-next"
            >
              Next
            </Button>
          </div>
        </section>
      )}

      {/* Step 2: Meeting Notes + Generate */}
      {step === 2 && (
        <section className="mt-8" data-testid="step-2">
          <SectionRule label="Meeting notes" />
          <div className="space-y-4">
            {templates.length > 0 && (
              <Field label="Template" htmlFor="proposal-template" hint="Optional: the AI uses it as a starting point">
                <Select value={selectedTemplate} onValueChange={(val) => {
                  setSelectedTemplate(val);
                  if (val && val !== 'none') {
                    const tpl = templates.find(t => t.id === val);
                    if (tpl?.template_content) {
                      toast.info('Template loaded. AI will use this as a starting point.');
                    }
                  }
                }}>
                  <SelectTrigger id="proposal-template" data-testid="select-template">
                    <SelectValue placeholder="No template, start fresh" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No template</SelectItem>
                    {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field label="Notes, transcript, or project brief" htmlFor="proposal-notes">
              <Textarea
                id="proposal-notes"
                value={meetingNotes}
                onChange={e => setMeetingNotes(e.target.value)}
                placeholder="Paste your meeting notes here. Include the project scope, client requirements, timeline, and anything else that matters."
                className="min-h-[240px]"
                data-testid="meeting-notes-textarea"
              />
            </Field>
          </div>

          <div className="sticky bottom-0 z-10 mt-10 flex justify-between gap-3 border-t border-line bg-paper/95 py-4">
            <Button variant="ghost" onClick={() => setStep(1)}>Back</Button>
            <Button onClick={handleGenerate} disabled={generating} data-testid="generate-proposal-btn">
              {generating ? (
                <><Loader2 strokeWidth={1.5} className="animate-spin" /> Writing your proposal…</>
              ) : 'Generate with AI'}
            </Button>
          </div>
        </section>
      )}

      {/* Step 3: Edit & Save */}
      {step === 3 && content && (
        <div className="mt-8" data-testid="step-3">
          <section>
            <SectionRule label="Overview" />
            <div className="space-y-4">
              <Field label="Proposal title" htmlFor="proposal-title">
                <Input id="proposal-title" value={title} onChange={e => setTitle(e.target.value)} data-testid="proposal-title-input" />
              </Field>
              <Field label="Summary" htmlFor="proposal-summary">
                <Textarea id="proposal-summary" value={content.summary || ''} onChange={e => setContent({ ...content, summary: e.target.value })} data-testid="proposal-summary" />
              </Field>
            </div>
          </section>

          <section className="mt-10">
            <SectionRule
              label="Client needs"
              action={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setContent({ ...content, client_needs: [...(content.client_needs || []), ''] })}
                  data-testid="add-client-need"
                >
                  Add need
                </Button>
              }
            />
            <div className="space-y-2">
              {(content.client_needs || []).map((need, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input
                    value={need}
                    aria-label={`Client need ${i + 1}`}
                    onChange={e => {
                      const needs = [...(content.client_needs || [])];
                      needs[i] = e.target.value;
                      setContent({ ...content, client_needs: needs });
                    }}
                    data-testid={`client-need-${i}`}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0 text-ink-subtle hover:text-destructive"
                    onClick={() => {
                      const needs = (content.client_needs || []).filter((_, idx) => idx !== i);
                      setContent({ ...content, client_needs: needs });
                    }}
                    aria-label={`Remove client need ${i + 1}`}
                    data-testid={`remove-client-need-${i}`}
                  >
                    <Trash2 strokeWidth={1.5} />
                  </Button>
                </div>
              ))}
              {(content.client_needs || []).length === 0 && (
                <p className="text-small text-ink-subtle">No needs listed yet.</p>
              )}
            </div>
          </section>

          <section className="mt-10">
            <SectionRule label="Solution" />
            <Field label="Proposed solution" htmlFor="proposal-solution">
              <Textarea
                id="proposal-solution"
                value={content.solution || ''}
                onChange={e => setContent({ ...content, solution: e.target.value })}
                className="min-h-[160px]"
                data-testid="proposal-solution"
              />
            </Field>
          </section>

          <section className="mt-10">
            <SectionRule
              label="Timeline"
              action={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setContent({ ...content, timeline: [...(content.timeline || []), { milestone: '', duration: '', description: '' }] })}
                  data-testid="add-timeline-row"
                >
                  Add row
                </Button>
              }
            />
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Milestone</TableHead>
                  <TableHead className="w-[130px]">Duration</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-10"><span className="sr-only">Remove</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(content.timeline || []).map((item, i) => (
                  <TableRow key={i} className="hover:bg-transparent" data-testid={`timeline-row-${i}`}>
                    <TableCell className="px-1">
                      <Input value={item.milestone} aria-label={`Milestone ${i + 1}`} onChange={e => { const t = [...(content.timeline || [])]; t[i] = { ...t[i], milestone: e.target.value }; setContent({ ...content, timeline: t }); }} placeholder="Milestone" />
                    </TableCell>
                    <TableCell className="px-1">
                      <Input value={item.duration} aria-label={`Milestone ${i + 1} duration`} onChange={e => { const t = [...(content.timeline || [])]; t[i] = { ...t[i], duration: e.target.value }; setContent({ ...content, timeline: t }); }} placeholder="Duration" />
                    </TableCell>
                    <TableCell className="px-1">
                      <Input value={item.description} aria-label={`Milestone ${i + 1} description`} onChange={e => { const t = [...(content.timeline || [])]; t[i] = { ...t[i], description: e.target.value }; setContent({ ...content, timeline: t }); }} placeholder="Description" />
                    </TableCell>
                    <TableCell className="px-1 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-ink-subtle hover:text-destructive"
                        onClick={() => {
                          const t = (content.timeline || []).filter((_, idx) => idx !== i);
                          setContent({ ...content, timeline: t });
                        }}
                        aria-label={`Remove milestone ${i + 1}`}
                        data-testid={`remove-timeline-${i}`}
                      >
                        <Trash2 strokeWidth={1.5} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </section>

          <section className="mt-10">
            <SectionRule
              label="Pricing"
              action={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setContent({ ...content, pricing: [...(content.pricing || []), { item: '', amount: 0, description: '' }] })}
                  data-testid="add-pricing-row"
                >
                  Add row
                </Button>
              }
            />
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="w-[130px] text-right">Amount</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-10"><span className="sr-only">Remove</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(content.pricing || []).map((item, i) => (
                  <TableRow key={i} className="hover:bg-transparent" data-testid={`pricing-row-${i}`}>
                    <TableCell className="px-1">
                      <Input value={item.item} aria-label={`Pricing item ${i + 1}`} onChange={e => { const p = [...(content.pricing || [])]; p[i] = { ...p[i], item: e.target.value }; setContent({ ...content, pricing: p }); }} placeholder="Item" />
                    </TableCell>
                    <TableCell className="px-1">
                      <Input
                        type="number"
                        value={item.amount}
                        aria-label={`Pricing item ${i + 1} amount`}
                        className="text-right tabular-nums"
                        onChange={e => {
                          const p = [...(content.pricing || [])];
                          p[i] = { ...p[i], amount: e.target.value };
                          setContent({ ...content, pricing: p });
                          setTotalAmount(p.reduce((s, x) => s + (parseFloat(x.amount) || 0), 0));
                        }}
                        onBlur={() => {
                          const p = [...(content.pricing || [])];
                          if (p[i].amount === '') {
                            p[i] = { ...p[i], amount: 0 };
                            setContent({ ...content, pricing: p });
                          }
                        }}
                        placeholder="Amount"
                      />
                    </TableCell>
                    <TableCell className="px-1">
                      <Input value={item.description} aria-label={`Pricing item ${i + 1} description`} onChange={e => { const p = [...(content.pricing || [])]; p[i] = { ...p[i], description: e.target.value }; setContent({ ...content, pricing: p }); }} placeholder="Description" />
                    </TableCell>
                    <TableCell className="px-1 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-ink-subtle hover:text-destructive"
                        onClick={() => {
                          const p = (content.pricing || []).filter((_, idx) => idx !== i);
                          setContent({ ...content, pricing: p });
                          setTotalAmount(p.reduce((s, x) => s + (x.amount || 0), 0));
                        }}
                        aria-label={`Remove pricing item ${i + 1}`}
                        data-testid={`remove-pricing-${i}`}
                      >
                        <Trash2 strokeWidth={1.5} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <dl className="ml-auto mt-6 max-w-[280px]">
              <div className="flex items-baseline justify-between border-t border-line-strong pt-3">
                <dt className="text-micro uppercase text-ink-subtle">Total</dt>
                <dd className="font-sans text-metric tabular-nums text-ink">
                  ${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </dd>
              </div>
            </dl>
          </section>

          <section className="mt-10">
            <SectionRule label="Terms" />
            <Field label="Terms" htmlFor="proposal-terms">
              <Textarea id="proposal-terms" value={content.terms || ''} onChange={e => setContent({ ...content, terms: e.target.value })} data-testid="proposal-terms" />
            </Field>
          </section>

          <div className="sticky bottom-0 z-10 mt-10 flex justify-between gap-3 border-t border-line bg-paper/95 py-4">
            <Button variant="ghost" onClick={() => setStep(2)}>Back</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-proposal-btn">
              {saving ? 'Saving…' : 'Save proposal'}
            </Button>
          </div>
        </div>
      )}

      {/* Add Client Dialog */}
      <Dialog open={showAddClient} onOpenChange={setShowAddClient}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add new client</DialogTitle>
            <DialogDescription>Quick add a client for this proposal.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Name" htmlFor="quick-client-name" hint="Required">
              <Input id="quick-client-name" value={newClient.name} onChange={e => setNewClient({ ...newClient, name: e.target.value })} data-testid="quick-client-name" />
            </Field>
            <Field label="Email" htmlFor="quick-client-email">
              <Input id="quick-client-email" value={newClient.email} onChange={e => setNewClient({ ...newClient, email: e.target.value })} />
            </Field>
            <Field label="Company" htmlFor="quick-client-company">
              <Input id="quick-client-company" value={newClient.company} onChange={e => setNewClient({ ...newClient, company: e.target.value })} />
            </Field>
            <div className="flex justify-end gap-3 border-t border-line pt-4">
              <Button variant="ghost" onClick={() => setShowAddClient(false)}>Cancel</Button>
              <Button onClick={handleAddClient} data-testid="quick-save-client">Add client</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
